import { useCallback, useState } from 'react';
import { Node, Edge } from '@xyflow/react';
import dagre from '@dagrejs/dagre';
import ELK from 'elkjs/lib/elk.bundled.js';

type LayoutType = 'dagre' | 'elk' | 'none';

export const useLayoutedElements = () => {
  const [layoutType, setLayoutType] = useState<LayoutType>('elk');
  const [isLayouting, setIsLayouting] = useState(false);

  // Function to apply dagre layout
  const getDagreLayoutedElements = useCallback((nodes: Node[], edges: Edge[], direction = 'BT') => {
    if (!nodes.length) return { nodes, edges };

    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));
    dagreGraph.setGraph({ rankdir: direction, ranker: 'tight-tree' });

    // Add nodes to dagre graph
    nodes.forEach((node) => {
      dagreGraph.setNode(node.id, { width: 150, height: 40 });
    });

    // Add edges to dagre graph
    edges.forEach((edge) => {
      dagreGraph.setEdge(edge.source, edge.target);
    });

    // Calculate layout
    dagre.layout(dagreGraph);

    // Apply layout to nodes
    const layoutedNodes = nodes.map((node) => {
      const nodeWithPosition = dagreGraph.node(node.id);
      return {
        ...node,
        position: {
          x: nodeWithPosition.x - 75, // Center the node
          y: nodeWithPosition.y - 20,
        },
      };
    });

    return { nodes: layoutedNodes, edges };
  }, []);

  // Function to apply ELK layout
  const getElkLayoutedElements = useCallback(
    async (nodes: Node[], edges: Edge[], direction = 'DOWN') => {
      if (!nodes.length) return { nodes, edges };

      const elk = new ELK();

      // Convert direction to ELK direction format
      const elkDirection =
        direction === 'BT'
          ? 'DOWN'
          : direction === 'TB'
            ? 'UP'
            : direction === 'LR'
              ? 'RIGHT'
              : direction === 'RL'
                ? 'LEFT'
                : direction;

      // Prepare the graph for ELK
      const elkGraph = {
        id: 'root',
        layoutOptions: {
          'elk.algorithm': 'layered',
          'elk.direction': elkDirection,
          'elk.spacing.nodeNode': '50',
          'elk.layered.spacing.nodeNodeBetweenLayers': '200',
          'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
          'elk.layered.mergeEdges': 'true',
          'elk.edgeRouting': 'SPLINES', // Routing style: ORTHOGONAL, SPLINES, POLYLINE
          'elk.layered.thoroughness': '7', // 1 (fast) to 7 (thorough)
          'elk.layered.spacing.edgeEdgeBetweenLayers': '15',
          'elk.layered.spacing.edgeEdgeBetweenNodes': '15',
        },
        children: nodes.map((node) => ({
          id: node.id,
          width: 150,
          height: 40,
        })),
        edges: edges.map((edge) => ({
          id: edge.id,
          sources: [edge.source],
          targets: [edge.target],
        })),
      };

      // Calculate layout
      const elkLayout = await elk.layout(elkGraph);

      // Apply layout to nodes
      const layoutedNodes = nodes.map((node) => {
        const elkNode = elkLayout.children?.find((n) => n.id === node.id);
        if (elkNode) {
          return {
            ...node,
            position: {
              x: elkNode.x || 0,
              y: elkNode.y || 0,
            },
          };
        }
        return node;
      });

      return { nodes: layoutedNodes, edges };
    },
    []
  );

  // Function to get layouted elements based on the selected layout type
  const getLayoutedElements = useCallback(
    async (nodes: Node[], edges: Edge[], direction = 'BT') => {
      if (layoutType === 'dagre') {
        return getDagreLayoutedElements(nodes, edges, direction);
      } else if (layoutType === 'elk') {
        setIsLayouting(true);
        try {
          const result = await getElkLayoutedElements(nodes, edges, direction);
          return result;
        } finally {
          setIsLayouting(false);
        }
      }
      // Default to no layout
      return { nodes, edges };
    },
    [layoutType, getDagreLayoutedElements, getElkLayoutedElements]
  );

  // Function to apply layout and update state
  const applyLayout = useCallback(
    (
      nodes: Node[],
      edges: Edge[],
      setNodes: React.Dispatch<React.SetStateAction<Node[]>>,
      setEdges: React.Dispatch<React.SetStateAction<Edge[]>>,
      direction = 'BT'
    ) => {
      setIsLayouting(true);

      if (layoutType === 'dagre') {
        const { nodes: layoutedNodes, edges: layoutedEdges } = getDagreLayoutedElements(
          nodes,
          edges,
          direction
        );
        setNodes([...layoutedNodes]);
        setEdges([...layoutedEdges]);
        setIsLayouting(false);
      } else if (layoutType === 'elk') {
        getElkLayoutedElements(nodes, edges, direction)
          .then(({ nodes: layoutedNodes, edges: layoutedEdges }) => {
            setNodes([...layoutedNodes]);
            setEdges([...layoutedEdges]);
          })
          .finally(() => {
            setIsLayouting(false);
          });
      } else {
        setIsLayouting(false);
      }
    },
    [layoutType, getDagreLayoutedElements, getElkLayoutedElements]
  );

  return {
    layoutType,
    setLayoutType,
    isLayouting,
    getLayoutedElements,
    applyLayout,
  };
};
